<script lang="ts">
	import { resolve } from '$app/paths';
	import DialogCreateIssueReport from '$lib/components/Dialogs/DialogCreateIssueReport.svelte';
	import * as Avatar from '$lib/components/ui/avatar';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu';
	import * as Sheet from '$lib/components/ui/sheet/index.js';
	import * as m from '$lib/paraglide/messages.js';
	import { localizeHref } from '@/paraglide/runtime';
	import { currentUser, pb } from '@/utils/ApiClient';
	import { logout } from '@/utils/Users.svelte';
	import Bug from '@lucide/svelte/icons/bug';
	import Heart from '@lucide/svelte/icons/heart';
	import LifeBuoy from '@lucide/svelte/icons/life-buoy';
	import LogOut from '@lucide/svelte/icons/log-out';
	import Settings from '@lucide/svelte/icons/settings';
	import Shield from '@lucide/svelte/icons/shield';

	type Mode = 'dropdown' | 'sheet-trigger';
	type NavVariant = 'overlay' | 'solid';

	interface Props {
		mode: Mode;
		compact?: boolean;
		navVariant?: NavVariant;
		sheetOpen?: boolean;
	}

	let {
		mode,
		compact = false,
		navVariant = 'solid',
		sheetOpen = $bindable(false)
	}: Props = $props();

	let openError = $state(false);

	const avatarUrl = $derived(
		$currentUser?.avatar ? pb.files.getURL($currentUser, $currentUser.avatar) : ''
	);
	const initials = $derived($currentUser?.username?.slice(0, 2).toUpperCase() || 'U');
	const avatarRingClass = $derived(
		navVariant === 'overlay' ? 'ring-2 ring-zp-paper/90 ring-offset-1 ring-offset-transparent' : ''
	);

	const avatarClass = $derived(compact ? 'size-6' : 'size-[calc(0.875rem*2+1.25*0.86rem)]');

	const triggerClass = $derived(
		compact
			? `inline-flex items-center rounded-full focus-visible:ring-2 focus-visible:ring-zp-ink/30 focus-visible:ring-offset-2 focus-visible:outline-none ${avatarRingClass}`
			: `rounded-full focus-visible:ring-2 focus-visible:ring-zp-ink/30 focus-visible:ring-offset-2 focus-visible:outline-none ${avatarRingClass}`
	);

	const avatarFallbackClass = $derived(
		compact ? 'bg-zp-paper2 text-[0.6rem] text-zp-ink' : 'bg-zp-paper2 text-sm text-zp-ink'
	);

	function closeSheet() {
		sheetOpen = false;
	}

	function handleLogout() {
		logout();
		closeSheet();
	}
</script>

{#if $currentUser}
	{#if mode === 'dropdown'}
		<DropdownMenu.Root>
			<DropdownMenu.Trigger class={triggerClass} aria-label="Menú de cuenta">
				<Avatar.Root class={avatarClass}>
					<Avatar.Image src={avatarUrl} alt="" class="object-cover" />
					<Avatar.Fallback class={avatarFallbackClass}>{initials}</Avatar.Fallback>
				</Avatar.Root>
			</DropdownMenu.Trigger>
			<DropdownMenu.Content align="end" class="z-50 w-56">
				<div class="px-2 py-1.5">
					<p class="text-sm font-medium text-zp-ink">{$currentUser.username}</p>
					<p class="truncate text-xs text-zp-muted">{$currentUser.email}</p>
				</div>
				<DropdownMenu.Separator />
				<DropdownMenu.Item>
					<a href={localizeHref('/cuenta')} class="flex w-full items-center gap-2 outline-none">
						<Settings class="size-4" />
						{m.sad_quaint_zebra_spin()}
					</a>
				</DropdownMenu.Item>
				<DropdownMenu.Item>
					<a
						href={resolve(localizeHref('/cuenta/favoritos'))}
						class="flex w-full items-center gap-2 outline-none"
					>
						<Heart class="size-4 text-red-500" />
						{m.sleek_neat_otter_peel()}
					</a>
				</DropdownMenu.Item>
				{#if $currentUser.role === 'staff'}
					<DropdownMenu.Item>
						<a
							href={resolve(localizeHref('/admin'))}
							class="flex w-full items-center gap-2 outline-none"
						>
							<Shield class="size-4 text-emerald-700" />
							Admin
						</a>
					</DropdownMenu.Item>
				{/if}
				<DropdownMenu.Separator />
				<DropdownMenu.Item>
					<a
						href="https://zarpar.eu/ayuda"
						target="_blank"
						rel="noopener noreferrer"
						class="flex w-full items-center gap-2 outline-none"
					>
						<LifeBuoy class="size-4 text-orange-500" />
						{m.slimy_home_goat_tap()}
					</a>
				</DropdownMenu.Item>
				<DropdownMenu.Item onclick={() => (openError = true)}>
					<Bug class="size-4" />
					{m.polite_stale_parakeet_revive()}
				</DropdownMenu.Item>
				<DropdownMenu.Separator />
				<DropdownMenu.Item onclick={() => logout()}>
					<LogOut class="size-4" />
					Cerrar sesión
				</DropdownMenu.Item>
			</DropdownMenu.Content>
		</DropdownMenu.Root>
	{:else}
		<button
			type="button"
			onclick={() => (sheetOpen = true)}
			aria-label="Menú de cuenta"
			aria-expanded={sheetOpen}
			aria-haspopup="dialog"
			class="grid size-11 shrink-0 place-items-center justify-self-end rounded-[9px] border border-zp-ink/10 bg-zp-paper p-0.5"
		>
			<Avatar.Root class="size-full rounded-[7px]">
				<Avatar.Image src={avatarUrl} alt="" class="object-cover" />
				<Avatar.Fallback
					class="rounded-[7px] bg-zp-navy text-[0.65rem] font-semibold text-zp-paper"
				>
					{initials}
				</Avatar.Fallback>
			</Avatar.Root>
		</button>

		<Sheet.Root bind:open={sheetOpen}>
			<Sheet.Content
				side="bottom"
				showCloseButton={false}
				class="nav-sheet z-75 gap-0 rounded-t-[18px] border-zp-line bg-zp-paper px-5 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-[0_-20px_44px_-20px_rgba(27,24,64,0.35)] md:hidden"
			>
				<Sheet.Header class="sr-only">
					<Sheet.Title>Mi cuenta</Sheet.Title>
					<Sheet.Description>Acceso a cuenta, favoritos y más</Sheet.Description>
				</Sheet.Header>
				<div class="mx-auto mb-4 h-1 w-10 rounded-full bg-zp-line" aria-hidden="true"></div>
				<div class="mb-4 px-1">
					<p class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
						{$currentUser.username}
					</p>
					<p class="mt-0.5 truncate text-[0.82rem] text-zp-muted">{$currentUser.email}</p>
				</div>
				<nav class="flex flex-col">
					<a
						href={localizeHref('/cuenta')}
						onclick={closeSheet}
						class="flex min-h-11 items-center justify-between gap-3 border-b border-zp-line py-3.5 first:border-t"
					>
						<span class="flex items-center gap-2.5">
							<Settings class="size-4 shrink-0 text-zp-muted" />
							<span class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
								{m.sad_quaint_zebra_spin()}
							</span>
						</span>
						<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
					</a>
					<a
						href={resolve(localizeHref('/cuenta/favoritos'))}
						onclick={closeSheet}
						class="flex min-h-11 items-center justify-between gap-3 border-b border-zp-line py-3.5"
					>
						<span class="flex items-center gap-2.5">
							<Heart class="size-4 shrink-0 text-red-500" />
							<span class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
								{m.sleek_neat_otter_peel()}
							</span>
						</span>
						<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
					</a>
					{#if $currentUser.role === 'staff'}
						<a
							href={resolve(localizeHref('/admin'))}
							onclick={closeSheet}
							class="flex min-h-11 items-center justify-between gap-3 border-b border-zp-line py-3.5"
						>
							<span class="flex items-center gap-2.5">
								<Shield class="size-4 shrink-0 text-emerald-700" />
								<span
									class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink"
								>
									Admin
								</span>
							</span>
							<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
						</a>
					{/if}
					<a
						href="https://zarpar.eu/ayuda"
						target="_blank"
						rel="noopener noreferrer"
						onclick={closeSheet}
						class="flex min-h-11 items-center justify-between gap-3 border-b border-zp-line py-3.5"
					>
						<span class="flex items-center gap-2.5">
							<LifeBuoy class="size-4 shrink-0 text-orange-500" />
							<span class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
								{m.slimy_home_goat_tap()}
							</span>
						</span>
						<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
					</a>
					<button
						type="button"
						onclick={() => {
							openError = true;
							closeSheet();
						}}
						class="flex min-h-11 w-full items-center justify-between gap-3 border-b border-zp-line py-3.5 text-left"
					>
						<span class="flex items-center gap-2.5">
							<Bug class="size-4 shrink-0 text-zp-muted" />
							<span class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
								{m.polite_stale_parakeet_revive()}
							</span>
						</span>
						<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
					</button>
					<button
						type="button"
						onclick={handleLogout}
						class="flex min-h-11 w-full items-center justify-between gap-3 border-b border-zp-line py-3.5 text-left"
					>
						<span class="flex items-center gap-2.5">
							<LogOut class="size-4 shrink-0 text-zp-muted" />
							<span class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
								Cerrar sesión
							</span>
						</span>
						<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
					</button>
				</nav>
				<button
					type="button"
					onclick={closeSheet}
					class="mt-4 w-full rounded-[9px] border border-zp-line py-3 text-[0.86rem] font-semibold text-zp-ink"
				>
					Cerrar
				</button>
			</Sheet.Content>
		</Sheet.Root>
	{/if}
{/if}

<DialogCreateIssueReport bind:open={openError} />

<style>
	:global(.nav-sheet) {
		font-family:
			'Geist',
			system-ui,
			-apple-system,
			sans-serif;
		font-size: 17px;
		line-height: 1.6;
		color: var(--color-zp-ink);
		-webkit-font-smoothing: antialiased;
		text-rendering: optimizeLegibility;
	}

	:global(.nav-sheet a) {
		text-decoration: none;
	}

	:global(.nav-sheet .font-serif) {
		font-family: 'Newsreader', Georgia, 'Times New Roman', serif;
		font-weight: 400;
		letter-spacing: -0.018em;
	}
</style>
